import type { ParsedField } from './labelPrintTypes';
import { splitTextFields } from '../../utils/LabelFieldMapper';
import { buildQrStyle, escapeHtml } from './labelPrintHelpers';

/** Check if a field is an admin code or trial ID field (both share the same large-bold style) */
const isAdminCodeField = (field: ParsedField): boolean => {
    const norm = field.key.toLowerCase().replace(/[\s_]/g, '');
    return norm === 'admincode' || field.key.toLowerCase().includes('admin')
        || norm === 'trialid' || norm === 'trailid' || norm === 'trialbid'
        || norm === 'traidbid' || norm === 'traidid';
};

/** Render text spans with differentiated classes for admin code */
const renderTextSpansWithAdminCode = (fields: ParsedField[], cssClass: string): string => {
    return fields.map((f) => {
        const cls = isAdminCodeField(f) ? 'admin-code' : cssClass;
        return `<span class="${cls}">${f.value ? escapeHtml(f.value) : '&nbsp;'}</span>`;
    }).join('\n                ');
};

/** Render a QR cell wrapping an SVG */
const qrCell = (svg: string, qrMm: number): string =>
    `<div class="qr-cell"><div style="${buildQrStyle(qrMm)}">${svg}</div></div>`;

/** Render a QR cell with a caption below the code */
const qrCellWithCaption = (svg: string, qrMm: number, caption: string): string =>
    `<div class="qr-cell">
                <div style="${buildQrStyle(qrMm)}">${svg}</div>
                <span class="splot-id">${escapeHtml(caption)}</span>
            </div>`;

/** Horizontal layout: QR left/right + text beside */
const buildHorizontalBody = (
    qrFields: ParsedField[],
    textFields: ParsedField[],
    qrSvgs: string[],
    qrMm: number,
    isMaterialMode: boolean = true,
): string => {
    const { topText, bottomText } = splitTextFields(textFields);
    
    // SPLot mode: single-row layout with QR on left, all text on right, occupies full height
    if (!isMaterialMode) {
        if (qrFields.length >= 1) {
            return `
        <div class="label-row">
            ${qrCell(qrSvgs[0] ?? '', qrMm)}
            <div class="text-cell">
                <span class="splot-id">${escapeHtml(qrFields[0].value)}</span>
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
        </div>`;
        }
        return `
        <div class="label-row">
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
        </div>`;
    }

    // Material mode: four-row layout (banner / QR+text / text+QR / container ID footer)
    if (qrFields.length >= 2) {
        return `
        <div class="label-row material-banner-row">
            <span class="material-id-banner">${escapeHtml(qrFields[0].value)}</span>
        </div>
        <div class="label-row material-qr-row">
            ${qrCell(qrSvgs[0] ?? '', qrMm)}
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(topText, 'origin')}
            </div>
        </div>
        <div class="label-row material-bottom-row">
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(bottomText, 'footer-text')}
            </div>
            <div class="material-qr-with-id">
                ${qrCell(qrSvgs[1] ?? '', qrMm)}
            </div>
        </div>
        <div class="label-row material-footer-row">
            <span class="container-id">${escapeHtml(qrFields[1].value)}</span>
        </div>`;
    }

    if (qrFields.length === 1) {
        return `
        <div class="label-row material-banner-row">
            <span class="material-id-banner">${escapeHtml(qrFields[0].value)}</span>
        </div>
        <div class="label-row material-qr-row">
            ${qrCell(qrSvgs[0] ?? '', qrMm)}
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(topText, 'origin')}
            </div>
        </div>
        <div class="label-row material-bottom-row">
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(bottomText, 'footer-text')}
            </div>
        </div>
        <div class="label-row material-footer-row"></div>`;
    }

    return `
        <div class="label-row">
            <div class="text-cell">
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
        </div>`;
};

/** Vertical layout: QR on top, text stacked below */
const buildVerticalBody = (
    qrFields: ParsedField[],
    textFields: ParsedField[],
    qrSvgs: string[],
    qrMm: number,
): string => {
    if (qrFields.length >= 2) {
        return `
        <div class="label-row-vertical">
            ${qrCell(qrSvgs[0] ?? '', qrMm)}
            <div class="text-cell-center">
                <span class="splot-id">${escapeHtml(qrFields[0].value)}</span>
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
            ${qrCellWithCaption(qrSvgs[1] ?? '', qrMm, qrFields[1].value)}
        </div>`;
    }

    if (qrFields.length === 1) {
        return `
        <div class="label-row-vertical">
            ${qrCell(qrSvgs[0] ?? '', qrMm)}
            <div class="text-cell-center">
                <span class="splot-id">${escapeHtml(qrFields[0].value)}</span>
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
        </div>`;
    }

    return `
        <div class="label-row-vertical">
            <div class="text-cell-center">
                ${renderTextSpansWithAdminCode(textFields, 'origin')}
            </div>
        </div>`;
};

/** Pick layout builder based on orientation */
export const buildDynamicBody = (
    qrFields: ParsedField[],
    textFields: ParsedField[],
    qrSvgs: string[],
    qrMm: number,
    horizontal: boolean,
    isMaterialMode: boolean = true,
): string => horizontal
    ? buildHorizontalBody(qrFields, textFields, qrSvgs, qrMm, isMaterialMode)
    : buildVerticalBody(qrFields, textFields, qrSvgs, qrMm);
